<template>
  <v-row no-gutters>
    <v-col v-if="isFetched">
      <v-row no-gutters class="pb-4">
        <v-col cols="12">
          <v-row no-gutters align="baseline">
            <v-col cols="auto" class="pr-4 font-weight-bold">
              {{ $i18n.t('admin.dynamicDictionaries.title') }}
            </v-col>
            <v-col cols="auto">
              <base-rounded-button
                icon="mdi-plus"
                size="x-small"
                color="success"
                :tooltip="$i18n.t('admin.dynamicDictionaries.addDictionary')"
                @click="openAddEditDictDialog(false)"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" style="border: 1px solid rgba(0, 0, 0, 0.12);">
          <v-data-table hide-default-footer disable-pagination :items="relationsDictionaries" :headers="headers">
            <template #[`item.actions`]="{ item }">
              <dots-menu
                :items="dotsMenuItems"
                @edit="openAddEditDictDialog(true, item)"
                @delete="openDeleteDialog(item)"
              ></dots-menu>
            </template>
            <template #[`item.datasource`]="{ item }">
              <span>{{ dataSources[item.datasource].verbose_name }}</span>
            </template>
            <template #[`item.attribute`]="{ item }">
              <span>{{ getDatasourceAttributeVerboseName(item.datasource, item.attribute) }}</span>
            </template>
            <template #[`item.representation`]="{ item }">
              <span>{{ getDatasourceAttributeVerboseName(item.datasource, item.representation) }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-else cols="12">
      <main-loader />
    </v-col>
    <main-dialog
      :is-visible.sync="isDeleteDictVisible"
      v-if="isDeleteDictLoaded"
      :title="$i18n.t('dialog.deleteDictionary')"
      :body="$i18n.t('dialog.confirmDictionaryDelete')"
      agree-text="dialog.agreeDelete"
      :loading="isDeleteDictLoading"
      @agree="handleDeleteDict"
    ></main-dialog>
    <main-input-dialog
      dialog-width="40%"
      v-if="isAddEditDictLoaded"
      :is-visible.sync="isAddEditDictVisible"
      :loading="isAddEditDictLoading"
      :title-translation="$i18n.t(`admin.dynamicDictionaries.${isDictEditing ? 'editDict' : 'addDict'}`)"
      :agree-text="`dialog.${isDictEditing ? 'agreeEdit' : 'agreeAdd'}`"
      @agree="handleDict"
      :inputs="dictInputs"
    >
      <template v-for="input of dictInputs" #[`${input.name}.input`]="data">
        <v-col :key="input.name" cols="6">
          <data-input
            v-model="addEditDictPayload[input.name]"
            v-bind="data"
            @change="onInputValueChange(input.name, $event)"
          />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>

<script>
import DotsMenu from '@/components/DotsMenu';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';

import { get, call } from 'vuex-pathify';

export default {
  name: 'DynamicDictionariesSettingsList',
  components: {
    DotsMenu,
    MainLoader,
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    isDeleteDictVisible: false,
    isDeleteDictLoaded: false,
    isDeleteDictLoading: false,
    isAddEditDictLoaded: false,
    isAddEditDictLoading: false,
    isAddEditDictVisible: false,
    isDictEditing: false,
    currentDictId: null,
    currentDataSourceAttributes: null,
    isFetched: false,
    addEditDictPayload: {},
    dotsMenuItems: [
      {
        name: 'edit',
      },
      {
        name: 'delete',
      },
    ],
  }),
  computed: {
    dataSources: get('layers/metadata'),
    relationsDictionaries: get('admin/relationsDictionaries'),
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources);
    },
    dictInputs() {
      return [
        {
          name: 'name',
          translationPath: 'admin.dynamicDictionaries.name',
          dataType: { name: 'text' },
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'datasource',
          translationPath: 'admin.dynamicDictionaries.datasource',
          dataType: { name: 'select' },
          items: this.dataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'representation',
          translationPath: 'admin.dynamicDictionaries.verboseAttribute',
          subtitle: 'admin.dynamicDictionaries.verboseAttributeSubtitle',
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes || [],
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.currentDataSourceAttributes,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'attribute',
          translationPath: 'admin.dynamicDictionaries.systemAttribute',
          subtitle: 'admin.dynamicDictionaries.systemAttributeSubtitle',
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes || [],
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.currentDataSourceAttributes,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    headers() {
      return [
        {
          text: this.$i18n.t('admin.dynamicDictionaries.name'),
          value: 'name',
          sortable: false,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.datasource'),
          value: 'datasource',
          sortable: false,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.verboseAttribute'),
          value: 'representation',
          sortable: false,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.systemAttribute'),
          value: 'attribute',
          sortable: false,
          align: 'center',
        },
        { text: '', value: 'actions', sortable: false },
      ];
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    ...call('admin/*RelationsDict*'),
    openAddEditDictDialog(isEditing = false, item) {
      this.addEditDictPayload = {};
      this.currentDataSourceAttributes = this.currentDictId = null;
      this.isDictEditing = isEditing;
      this.isAddEditDictVisible = this.isAddEditDictLoaded = true;
      if (isEditing) {
        this.currentDictId = item.id;
        this.currentDataSourceAttributes = this.dataSources[item.datasource]?.attributes_schema.attributes;
        let itemCopy = JSON.parse(JSON.stringify(item));
        ['attribute', 'representation'].forEach(name => {
          itemCopy[name] = this.currentDataSourceAttributes.find(attr => attr.id === itemCopy[name])?.name;
        });
        this.addEditDictPayload = JSON.parse(JSON.stringify(itemCopy));
      }
    },
    onInputValueChange(inputName, value) {
      if (inputName === 'datasource') {
        this.addEditDictPayload.attribute = this.addEditDictPayload.representation = null;
        this.currentDataSourceAttributes = this.dataSources[value]?.attributes_schema.attributes;
      }
    },
    openDeleteDialog(item) {
      this.currentDictId = item.id;
      this.isDeleteDictVisible = this.isDeleteDictLoaded = true;
    },
    getDatasourceAttributeVerboseName(datasource, attribute) {
      const searchedDatasource = this.dataSources[datasource];
      const datasourceAttributes = searchedDatasource.attributes_schema.attributes;
      return datasourceAttributes.find(attr => attr.id === attribute)?.verbose_name;
    },
    async handleDeleteDict() {
      this.isDeleteDictLoading = true;
      try {
        await this.deleteRelationsDictionary(this.currentDictId);
      } finally {
        this.isDeleteDictLoading = this.isDeleteDictVisible = false;
      }
    },
    async handleDict() {
      this.isAddEditDictLoading = true;
      try {
        if (this.isDictEditing) {
          const copyPayload = JSON.parse(JSON.stringify(this.addEditDictPayload));
          delete copyPayload.id;
          await this.putRelationsDictionary({
            id: this.currentDictId,
            payload: copyPayload,
          });
        } else {
          await this.postRelationsDictionary(this.addEditDictPayload);
        }
      } finally {
        this.isAddEditDictLoading = this.isAddEditDictVisible = false;
      }
    },
    async init() {
      await Promise.all([this.getDataSources({ with_attributes_schema: true }), this.getRelationsDictionaries()]);
      this.isFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>
